import { replace, is, repeat, toLower } from "ramda";

function capitalizeFirstLetter(input) {
  if (!is(String, input)) {
    return input;
  }

  return input.charAt(0).toUpperCase() + input.slice(1);
}

function capitalize(input) {
  if (!is(String, input)) {
    return input;
  }

  return input.replace(
    /\w\S*/g,
    (word) => word.charAt(0).toUpperCase() + word.substr(1)
  );
}

function initials(input, splitBy = " ") {
  if (!is(String, input)) {
    return input;
  }
  return input
    .split(splitBy)
    .reduce((output, word) => output + word[0], "")
    .toUpperCase();
}

function splitSentence(input, lineLength) {
  if (!is(String, input)) {
    return input;
  }

  let ref = 0;
  const lineArray = [""];
  input.split(" ").forEach((word) => {
    if (lineArray[ref].length + word.length > lineLength) {
      ref += 1;
      lineArray[ref] = word;
    } else {
      lineArray[ref] += ` ${word}`;
    }
  });

  return lineArray;
}

function camelCase(input = "") {
  if (!is(String, input)) {
    return input;
  }

  return input.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
    if (+match === 0) return "";
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
}

function removeHTMLTags(input) {
  if (!is(String, input)) {
    return input;
  }

  return replace(/<[^>]+>/g, "", input).replace(/\s/g, " ");
}

const parseBool = (stringBool) => {
  if (typeof stringBool !== "boolean") {
    if (is(String, stringBool)) {
      return toLower(stringBool) === "true";
    }

    return false;
  }

  return stringBool;
};

const parseBoolWithNull = (stringBool) => {
  if (typeof stringBool !== "boolean") {
    if (is(String, stringBool) && toLower(stringBool) === "true") {
      return true;
    }

    if (is(String, stringBool) && toLower(stringBool) === "false") {
      return false;
    }

    return null;
  }

  return stringBool;
};

const obfuscateEmail = (email) => {
  const atPos = email.indexOf("@");
  if (atPos < 1) {
    return email;
  }
  const emailUserNameLength = email.substring(0, atPos).length;
  const obfuscateFrom = Math.floor(emailUserNameLength / 2);
  const obfuscatedValues = repeat("*", emailUserNameLength - obfuscateFrom)
    .toString()
    .replace(/,/gi, "");
  // eslint-disable-next-line max-len
  return `${email.substring(
    0,
    obfuscateFrom
  )}${obfuscatedValues}${email.substring(atPos)}`;
};

// based on  https://gist.github.com/maximkott/31dfdd37d4c0fee499de453faca17c83

const mask = (
  value = "",
  maskTemplate = "",
  maskPatterns = {},
  autoCompleteFormatting
) => {
  let maskedValue = "";
  // array representation of string under test
  const valueParts = value.split("");
  // array representation of the mask
  const maskParts = maskTemplate.split("");

  // as long as there are still characters left in
  // the original string, one must try to mask them
  while (valueParts.length > 0) {
    // take the first character and remove
    // it from the original string
    let unmaskedChar = valueParts.shift();

    // as long as the character has not been masked
    // one must try to find a mask
    while (unmaskedChar !== null) {
      // take the first mask character from
      // the mask string
      const maskChar = maskParts.shift();

      // make sure the masking character exists
      // otherwise this means that the original string
      // exceeds the masking pattern length
      if (maskChar !== undefined) {
        // try to find a pattern for the particular
        // mask character
        const maskPattern = maskPatterns[maskChar];

        // if there is no pattern configured for
        // this particular mask character, assume
        // the mask character is a placeholder / formatting
        // value that must be added to the string
        if (maskPattern !== undefined) {
          let check = false;

          // mask pattern can be either a function
          if (typeof maskPattern === "function") {
            check = maskPattern(unmaskedChar, maskedValue);
          } else if (maskPattern instanceof RegExp) {
            // or a regex string
            check = maskPattern.test(unmaskedChar);
          }

          // if character has passed the mask check,
          // it can bee added to the final masked value
          if (check) {
            maskedValue += unmaskedChar;
          } else {
            // otherwise one must put the pattern back into
            // the array so the next character can try to
            // pass the mask check
            maskParts.unshift(maskChar);
          }

          unmaskedChar = null;
        } else {
          // mask character is a placeholder / formatting value
          // and must be added to the masked string
          maskedValue += maskChar;
        }
      } else {
        // no masking character could be found,
        // the original string is probably longer
        // then the mask pattern and therefore
        // the leftovers can be cut off
        // reset current character to continue the loop
        unmaskedChar = null;
      }
    }
  }
  if (autoCompleteFormatting) {
    // as long as there are still characters left in the mask string
    // add the formatting values in the current position
    while (maskParts.length > 0) {
      const maskChar = maskParts.shift();
      if (maskChar !== undefined && maskPatterns[maskChar] === undefined) {
        maskedValue += maskChar;
      } else {
        break;
      }
    }
  }
  return maskedValue;
};

function removeLeadingZeros(str) {
  return (str || "").replace(/\b0+/g, "");
}

function extractLineBreak(str = "") {
  return replace(/<p\/>|\r|\n*/g, "", str || "");
}

function extractTextFromName(str = "") {
  return replace(/<br>|<p[^>]*>|<\/p>|\r|<p\/>/g, "", str || "");
}

export {
  capitalizeFirstLetter,
  capitalize,
  initials,
  splitSentence,
  camelCase,
  removeHTMLTags,
  parseBoolWithNull,
  parseBool,
  obfuscateEmail,
  mask,
  removeLeadingZeros,
  extractLineBreak,
  extractTextFromName,
};
